/* eslint-disable @nx/enforce-module-boundaries */
import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { useMutation } from '@apollo/client';
import { Button, RichText, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { AIcon, AIconSize } from '@marriott/mi-web-ui-kit';
import { NGDatePicker, NGRoomsAndGuests } from '@marriott/mi-shop-components/src/molecules';
import {
  DatesFieldType,
  SearchFormState,
  StaticDataContextProvider,
  useInventoryDateStore,
  useSearchFormStore,
} from '@marriott/mi-shop-components';
import { WishListItemStoreType } from '@marriott/mi-store-utils';
import { phoenixAccountUpdateCustomerWishlist } from '../../modules/graph';
import { constants, localeDateFormats } from '../../modules/utils/constants/constants';
import { convertDateToLocaleSpecific, formatDateRange, generateApolloClientHeaders } from '../../modules/utils';
import {
  createWishlistsPayload,
  getTodayAndTomorrowDates,
  getWishlistDatePickerContextValue,
  getWishlistRoomsAndGuestsContextValue,
} from '../../modules/utils/wishlistHelper';
import {
  RoomsAndGuestsFormState,
  WishListMutationResponse,
  WishlistDetailViewHeaderProps,
} from './WishlistDetailView.types';

export const WishlistDetailViewHeader: React.FC<WishlistDetailViewHeaderProps> = ({
  authoredProps,
  currentLocale,
  currentWishlist,
  envVarsObject,
  fetchRates,
  isAuthorMode,
  isLocalDev,
  pageContext,
  pagePropertyIds,
  pageSize,
  sessionData,
  setWishListDetailsInfo,
  wishlist,
  wishlistId,
  ratesError,
}) => {
  const getNormalizedCount = (value: number | null | undefined, fallback: number) => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 1) {
      return fallback;
    }
    return value;
  };
  const dates = getTodayAndTomorrowDates();
  const updatedWishlistsRef = useRef<WishListItemStoreType[]>([]);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const roomsRef = useRef<HTMLDivElement>(null);
  const roomsAutoTriggerRef = useRef<HTMLDivElement>(null);
  const roomsCloseActionRef = useRef<'apply' | 'cancel' | null>(null);
  // Prevents the auto-apply useEffect from firing with stale storeDates on first open
  const datePickerJustOpenedRef = useRef(false);

  const [dateRoomError, setDateRoomError] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isRoomsOpen, setIsRoomsOpen] = useState(false);
  const [isWeekendButtonSelected, setIsWeekendButtonSelected] = useState(false);
  const isMobileViewPortAbove = !useCheckBreakpoint('viewportM');
  const isTabletViewOrBelow = !useCheckBreakpoint('viewportL');
  const [isDatesUpdating, setIsDatesUpdating] = useState(false);

  const setSearchFormState = useSearchFormStore((state: SearchFormState) => state.setSearchFormState);
  const storeDates = useSearchFormStore((state: SearchFormState) => state.dates);
  const storeRoomsAndGuests = useSearchFormStore((state: SearchFormState) => state.roomsAndGuests);
  const inventoryFetchStatus = useInventoryDateStore(state => state.fetchStatus);
  const setInventoryDates = useInventoryDateStore(state => state.setInventoryDates);
  const singleDateLimit = useInventoryDateStore(state => state.singleDateLimit);
  const hasSpecificDates = !!(currentWishlist?.startDate && currentWishlist?.endDate);
  const normalizedRoomsCount = getNormalizedCount(currentWishlist?.numberOfRooms, 1);
  const normalizedGuestsCount = getNormalizedCount(currentWishlist?.numberOfGuests, 1);
  const [datesButtonLabel, setDatesButtonLabel] = useState(
    hasSpecificDates
      ? formatDateRange(
          currentWishlist?.startDate ?? dates.todayDate,
          currentWishlist?.endDate ?? dates.tomorrowDate,
          currentLocale
        )
      : authoredProps?.modalDate || ''
  );
  const roomsButtonLabel = `${normalizedRoomsCount} ${
    normalizedRoomsCount === 1 ? authoredProps?.room : authoredProps?.rooms || ''
  }, ${normalizedGuestsCount} ${normalizedGuestsCount === 1 ? authoredProps?.guest : authoredProps?.guests}`;
  const datePickerContextValue = getWishlistDatePickerContextValue(authoredProps, envVarsObject?.['ACCEPT_LANGUAGE']);
  const roomsAndGuestsContextValue = getWishlistRoomsAndGuestsContextValue(authoredProps);

  const [updateWishlistDatesRooms] = useMutation<WishListMutationResponse>(phoenixAccountUpdateCustomerWishlist, {
    onCompleted: data => {
      const statusArr = data?.createCustomerWishlist?.status;
      const isSuccess =
        Array.isArray(statusArr) && statusArr?.some(status => status?.code?.toUpperCase() === constants.STATUS_SUCCESS);
      if (isSuccess) {
        setIsDatesUpdating(false);
        setWishListDetailsInfo({ wishlist: updatedWishlistsRef.current });
        setIsDatePickerOpen(false);
        setIsRoomsOpen(false);
        setDatesButtonLabel(
          formatDateRange(
            storeDates?.fromDate?.format(constants.STANDARD_DATE_FORMAT),
            storeDates?.toDate?.format(constants.STANDARD_DATE_FORMAT),
            currentLocale
          )
        );
        if (pagePropertyIds?.length > 0 && !isAuthorMode) {
          fetchRates({
            variables: {
              search: {
                ids: pagePropertyIds,
                options: {
                  startDate: storeDates?.fromDate?.format(constants.STANDARD_DATE_FORMAT) || '',
                  endDate: storeDates?.toDate?.format(constants.STANDARD_DATE_FORMAT) || '',
                  rateRequestTypes: [{ type: 'STANDARD', value: '' }],
                  customerId: '',
                },
              },
              limit: pageSize,
              offset: null,
            },
          });
        }
      } else {
        setIsDatesUpdating(false);
        setDateRoomError(true);
      }
    },
    onError: () => {
      setIsDatesUpdating(false);
      setDateRoomError(true);
    },
  });

  useEffect(() => {
    if (!isRoomsOpen) return;
    let timerId: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const maxAttempts = 8;
    const retryInterval = 80;

    const tryTrigger = () => {
      const trigger = roomsAutoTriggerRef?.current?.querySelector<HTMLElement>('.rooms-and-guests');
      if (trigger) {
        trigger.click();
      } else if (attempts++ < maxAttempts) {
        timerId = setTimeout(tryTrigger, retryInterval);
      }
    };

    const rafId = requestAnimationFrame(tryTrigger);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timerId);
    };
  }, [isRoomsOpen]);

  useEffect(() => {
    if (inventoryFetchStatus === 'idle') {
      const elevenMonthsAhead = new Date();
      elevenMonthsAhead?.setMonth(elevenMonthsAhead?.getMonth() + 11);
      const limitDate = new Date(elevenMonthsAhead?.getFullYear(), elevenMonthsAhead?.getMonth() + 1, 0)
        .toISOString()
        .slice(0, 10);
      setInventoryDates(limitDate, limitDate);
    }
  }, [inventoryFetchStatus, setInventoryDates]);

  // On desktop, NGDatePicker auto-closes after checkout date selection without triggering onDatePickerChange
  // (the Done CTA is hidden on desktop exact-dates mode). Watch the store and apply when both dates are set.
  // The ref guard skips the very first effect run when the picker opens so stale storeDates
  // (from a prior session) don't trigger an immediate apply+close.
  useEffect(() => {
    if (!isDatePickerOpen || isTabletViewOrBelow) {
      datePickerJustOpenedRef.current = false;
      return;
    }
    if (!datePickerJustOpenedRef.current) {
      datePickerJustOpenedRef.current = true;
      return;
    }
    if (storeDates?.fromDate && storeDates?.toDate && !storeDates?.flexible) {
      void handleDatePickerApply();
    }
  }, [storeDates, isDatePickerOpen, isTabletViewOrBelow]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDatePickerOpen && datePickerRef.current && !datePickerRef?.current?.contains(event.target as Node)) {
        setIsDatePickerOpen(false);
      }
      if (isRoomsOpen && roomsRef?.current && !roomsRef?.current?.contains(event.target as Node)) {
        setIsRoomsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDatePickerOpen, isRoomsOpen]);

  const handleOpenDatePicker = () => {
    setIsRoomsOpen(false);
    setDateRoomError(false);
    if (!isDatePickerOpen) {
      // Pre-populate the store with wishlist dates as moment objects so the calendar
      // renders the correct highlighted range. Falls back to today/tomorrow.
      const checkIn = currentWishlist?.startDate?.slice(0, 10) || dates.todayDate;
      const checkOut = currentWishlist?.endDate?.slice(0, 10) || dates.tomorrowDate;
      const fromMomentRaw = checkIn ? moment(checkIn, 'YYYY-MM-DD', true) : null;
      const toMomentRaw = checkOut ? moment(checkOut, 'YYYY-MM-DD', true) : null;
      // Only use parsed moments if they are valid; invalid moments crash react-day-picker
      const fromMomentValid = fromMomentRaw?.isValid() ? fromMomentRaw : null;
      const toMomentValid = toMomentRaw?.isValid() ? toMomentRaw : null;
      const today = moment().startOf('day');
      const limitMoment = singleDateLimit ? moment(singleDateLimit, 'YYYY-MM-DD', true) : null;
      // Only pre-select if both dates are within the valid booking range
      const isFromInRange = fromMomentValid && !fromMomentValid.isBefore(today);
      const isToInRange =
        toMomentValid && (!limitMoment || !toMomentValid.isAfter(limitMoment)) && !toMomentValid.isBefore(today);
      if (isFromInRange && isToInRange) {
        const nights = toMomentValid.diff(fromMomentValid, 'days');
        setSearchFormState(['dates'], {
          dates: {
            fromDate: fromMomentValid,
            toDate: toMomentValid,
            checkInDate: checkIn,
            checkOutDate: checkOut,
            flexible: false,
            numberOfNights: nights,
            lengthOfStay: nights,
          },
        });
      } else {
        // Dates are out of range — clear so the calendar opens with no pre-selection
        setSearchFormState(['dates'], {
          dates: {
            fromDate: null,
            toDate: null,
            checkInDate: '',
            checkOutDate: '',
            flexible: false,
            numberOfNights: 0,
            lengthOfStay: 0,
          },
        });
      }
    }
    setIsDatePickerOpen(isOpen => !isOpen);
  };

  const handleOpenRooms = () => {
    setIsDatePickerOpen(false);
    setDateRoomError(false);
    roomsCloseActionRef.current = null;
    setSearchFormState(['roomsAndGuests'], {
      roomsAndGuests: {
        numRooms: String(normalizedRoomsCount),
        numAdultsPerRoom: normalizedGuestsCount,
        numChildrenPerRoom: 0,
        childrenAges: [],
      },
    });
    setIsRoomsOpen(isOpen => !isOpen);
  };

  const hasRoomsAndGuestsChanged = (latestRooms: RoomsAndGuestsFormState | null | undefined) => {
    const nextNumberOfRooms = parseInt(latestRooms?.numRooms || '1', 10);
    const nextNumberOfGuests = (latestRooms?.numAdultsPerRoom ?? 1) + (latestRooms?.numChildrenPerRoom ?? 0);
    return nextNumberOfRooms !== normalizedRoomsCount || nextNumberOfGuests !== normalizedGuestsCount;
  };

  const handleDatePickerApply = async () => {
    const latestDates = storeDates as DatesFieldType | null | undefined;
    let checkIn = latestDates?.checkInDate || '';
    let checkOut = latestDates?.checkOutDate || '';

    if (latestDates?.fromDate && latestDates?.toDate) {
      checkIn = convertDateToLocaleSpecific(
        latestDates.fromDate?.format(constants.STANDARD_DATE_FORMAT),
        currentLocale,
        localeDateFormats[currentLocale?.replace('_', '-') ?? 'en-US']?.prefillFullDate
      );
      checkOut = convertDateToLocaleSpecific(
        latestDates.toDate?.format(constants.STANDARD_DATE_FORMAT),
        currentLocale,
        localeDateFormats[currentLocale?.replace('_', '-') ?? 'en-US']?.prefillFullDate
      );
    }

    const currentCheckIn = currentWishlist?.startDate?.slice(0, 10) || '';
    const currentCheckOut = currentWishlist?.endDate?.slice(0, 10) || '';

    if (!checkIn || !checkOut || (checkIn === currentCheckIn && checkOut === currentCheckOut)) {
      setIsDatePickerOpen(false);
      return;
    }

    setIsDatesUpdating(true);
    setIsDatePickerOpen(false);
    setDateRoomError(false);

    const updatedWishlists = (wishlist || []).map(item =>
      item.wishlistId === wishlistId
        ? {
            ...item,
            startDate: latestDates?.fromDate?.format(constants.STANDARD_DATE_FORMAT),
            endDate: latestDates?.toDate?.format(constants.STANDARD_DATE_FORMAT),
          }
        : item
    );
    updatedWishlistsRef.current = updatedWishlists;

    try {
      await updateWishlistDatesRooms({
        variables: {
          input: { customerId: sessionData?.consumerID, wishlists: createWishlistsPayload(updatedWishlists) },
        },
        context: generateApolloClientHeaders(isLocalDev, pageContext),
      });
    } catch {
      setIsDatesUpdating(false);
      setDateRoomError(true);
    }
  };

  const handleSaveRoomsAndGuests = async (roomsOverride?: RoomsAndGuestsFormState | null) => {
    setDateRoomError(false);
    const latestRooms = roomsOverride ?? storeRoomsAndGuests;
    const parsedRooms = parseInt(latestRooms?.numRooms || '1', 10);
    const numberOfRooms = getNormalizedCount(parsedRooms, 1);
    const parsedGuests = (latestRooms?.numAdultsPerRoom ?? 1) + (latestRooms?.numChildrenPerRoom ?? 0);
    const numberOfGuests = getNormalizedCount(parsedGuests, 1);
    const updatedWishlists = (wishlist || []).map(item =>
      item.wishlistId === wishlistId ? { ...item, numberOfRooms, numberOfGuests } : item
    );
    updatedWishlistsRef.current = updatedWishlists;

    try {
      await updateWishlistDatesRooms({
        variables: {
          input: { customerId: sessionData?.consumerID, wishlists: createWishlistsPayload(updatedWishlists) },
        },
        context: generateApolloClientHeaders(isLocalDev, pageContext),
      });
    } catch {
      setIsDatesUpdating(false);
      setDateRoomError(true);
    }
  };

  return (
    <>
      {!!currentWishlist && hasSpecificDates && (
        <div className="wishlist-detail-view__dropdown-wrapper" ref={datePickerRef}>
          <Button
            className={clsx(
              'm-button-s m-button-secondary d-inline-flex align-items-center',
              isDatesUpdating && 'disabled'
            )}
            ariaLabel={authoredProps?.dateModalHeader || 'Select dates'}
            ariaExpanded={isDatePickerOpen}
            callback={handleOpenDatePicker}
            testId="wishlist-detail-dates-btn"
            isDisabled={isDatesUpdating}
          >
            <AIcon icon="icon-stay-dates" size={AIconSize.S} className="mr-1" />
            {isDatesUpdating ? (
              <div className="m-spinner-s" data-testid="wishlist-dates-spinner" />
            ) : (
              <span>{datesButtonLabel}</span>
            )}
          </Button>
          {isDatePickerOpen && (
            <div
              className="wishlist-detail-view__dropdown wishlist-detail-view__dropdown--datepicker"
              data-testid="wishlist-detail-dates-dropdown"
            >
              <StaticDataContextProvider value={datePickerContextValue}>
                <div>
                  <NGDatePicker
                    onDatePickerChange={(available: boolean) => {
                      available && handleDatePickerApply();
                    }}
                    onDatePickerCancel={() => setIsDatePickerOpen(false)}
                    setDefaultDate={true}
                    isWeekendButtonSelected={isWeekendButtonSelected}
                    setIsWeekendButtonSelected={setIsWeekendButtonSelected}
                    isInModal={true}
                    isNextGenHP={true}
                    enableWeekendSelectorOnMobile={false}
                    isModalOpen={true}
                    isMobileTestView={isMobileViewPortAbove}
                    alignRight={true}
                  />
                </div>
              </StaticDataContextProvider>
            </div>
          )}
        </div>
      )}
      {!!currentWishlist && (
        <div
          className="wishlist-detail-view__dropdown-wrapper"
          ref={roomsRef}
          onClickCapture={event => {
            const target = event.target as HTMLElement;
            if (target.closest('.apply-button')) {
              roomsCloseActionRef.current = 'apply';
            }
          }}
        >
          <Button
            className="m-button-s m-button-secondary d-inline-flex align-items-center"
            ariaLabel={authoredProps?.roomsAndGuestEyebrowText}
            callback={handleOpenRooms}
            testId="wishlist-detail-rooms-btn"
          >
            <AIcon icon="icon-nav---account" size={AIconSize.S} className="mr-1" />
            <span>{roomsButtonLabel}</span>
          </Button>
          {isRoomsOpen && (
            <StaticDataContextProvider value={roomsAndGuestsContextValue}>
              <div
                className="wishlist-detail-view__dropdown wishlist-detail-view__dropdown--rooms p-0"
                data-testid="wishlist-detail-rooms-dropdown"
              >
                <div ref={roomsAutoTriggerRef}>
                  <NGRoomsAndGuests
                    isDesktopView={false}
                    isAvailabilityScenario={false}
                    isNextGenHp={true}
                    isInModal={true}
                    onCancel={() => {
                      const closeAction = roomsCloseActionRef.current;
                      roomsCloseActionRef.current = null;
                      setIsRoomsOpen(false);
                      if (closeAction === 'apply') {
                        const latestRooms = storeRoomsAndGuests as RoomsAndGuestsFormState | null | undefined;
                        if (hasRoomsAndGuestsChanged(latestRooms)) {
                          void handleSaveRoomsAndGuests(latestRooms ?? null);
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </StaticDataContextProvider>
          )}
        </div>
      )}
      {(dateRoomError || ratesError) && (
        <div className="wishlist-detail-view__error-message d-flex align-items-center flex-wrap mt-2">
          <AIcon icon="icon-warning" size={AIconSize.M} className="mr-1 alert-icon" />
          <RichText text={authoredProps?.wishlistDateRoomErrorMessage || ''} componentId="rooms-save-error" />
        </div>
      )}
    </>
  );
};
